// src/app/features/documents/DocumentRow.tsx
import React, { useState } from 'react';
import useMediaQueries from 'app/hooks/useMediaQueries';
import { IDirectoryNode } from 'app/services/documents';
import {
  BackIcon,
  DownloadIcon,
  FileIcon,
  FolderSolidIcon,
  OptionsIcon,
  WarningIcon,
} from 'assets/icons';
import { USDate } from 'Components/Dates/USDate';
import CheckboxInput from 'Components/Form/Inputs/CheckboxInput';
import { GridCell as Cell, GridRow as Row } from 'Components/Grid';
import Tooltip from 'Components/Tooltips';
import { saveAs } from 'file-saver';
import { useAuth } from 'utils/AuthProvider';
import { useDocumentsContext } from '.';

// Import or create a Modal component for popup functionality
import Modal from './DocumentsModal';

export function GridDocumentRow({
  node,
  index,
  rowType = 'Record',
  minimal = false,
}: {
  node: IDirectoryNode;
  index?: number;
  rowType?: 'Record' | 'Back';
  minimal?: boolean;
}) {
  const { isAuthenticated, isClient } = useAuth();
  const {
    filters,
    setFilters,
    ActionsPopupRef,
    selected,
    toggleSelectItem,
    canSelect,
    undo,
    canUndo,
  } = useDocumentsContext();
  const { lg_up: isDesktop } = useMediaQueries();
  const selectable = canSelect(node);
  const isSelected = !!selected[node.id];

  // State to track the URL for the modal popup
  const [fileModalUrl, setFileModalUrl] = useState<string | null>(null);

  // Extract the last viewed information
  const lastViewed = Array.isArray(node.viewed_file)
    ? node.viewed_file[0]
    : node.viewed_file;

  const DocumentName = () => {
    return (
      <div className="flex flex-wrap gap-2 items-start">
        <div
          className={`font-semibold flex items-center ${
            node.object_type
          } gap-2 ${rowType === 'Back' ? 'text-gray-700 ' : ''}`}
        >
          {rowType === 'Back' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (canUndo) {
                  undo();
                } else {
                  setFilters(
                    {
                      id: node.parent_id || undefined,
                      is_trashed: filters.is_trashed,
                    },
                    true,
                  );
                }
              }}
              className="cursor-pointer hover:shadow font-extrabold bg-gray-700 text-white py-1 px-2 rounded-xl flex gap-2"
            >
              <BackIcon className="w-5 h-5 text-white" />
            </button>
          )}
          <div className="flex gap-2 items-center">
            {node.object_type === 'folder' && (
              <FolderSolidIcon className="w-5 h-5 mr-3 shrink-0" />
            )}
            {node.object_type === 'document' && (
              <>
                {node.file_url ? (
                  <FileIcon className="w-5 h-5 mr-3 shrink-0" />
                ) : (
                  <Tooltip
                    message={
                      <div className="text-center">
                        File appears to be damaged or corrupted <br />
                        You can reupload it to fix this problem
                      </div>
                    }
                  >
                    <div className="relative w-6 h-6">
                      <FileIcon className="w-5 h-5 mr-3 shrink-0" />
                      <WarningIcon className="w-3 h-3 absolute bottom-1 right-1 " />
                    </div>
                  </Tooltip>
                )}
              </>
            )}
            <div>
              <span className="line-clamp-1 break-all text-ellipsis normal-case">
                {node.title}
              </span>
              {/* Subfolders and files count should be here */}
              {/* {node.object_type === 'folder' && (
                <p className="text-sm text-gray-500">
                  {node.subfolders_count || 0} folders,{' '}
                  {node.documents_count || 0} files
                </p>
              )} */}
              <USDate
                className="block text-xs font-normal text-gray-700"
                date={node.created_at}
              />
            </div>
          </div>
        </div>
        {/* {isDesktop && (
    <>
      {node.client && <Tag color="blue">{node.client?.name}</Tag>}
      {node.business && <Tag color="green">{node.business?.name}</Tag>}
      {node.task && <Tag color="red">{node.task?.title}</Tag>}
      {node.project && <Tag color="yellow">{node.project?.title}</Tag>}
    </>
  )} */}
        {/* {node.lastseenObject === 'true' */}
        {lastViewed && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              padding: '5px',
              marginRight: '10px',
              border: '1px solid #ddd',
              borderRadius: '5px',
              backgroundColor: '#f9f9f9',
              whiteSpace: 'nowrap',
            }}
          >
            <p
              style={{
                color: '#3B9B82',
                fontWeight: 'bold',
                margin: '0 5px 0 0',
                fontSize: '0.9em',
              }}
            >
              {lastViewed.user?.given_name || 'N/A'}{' '}
              {lastViewed.user?.family_name || ''}
            </p>
            <p
              style={{
                fontStyle: 'italic',
                margin: '0',
                color: '#555',
                fontSize: '0.8em',
              }}
            >
              seen at{' '}
              {lastViewed.viewed_at
                ? new Date(lastViewed.viewed_at).toLocaleString()
                : 'N/A'}
            </p>
          </div>
        )}
      </div>
    );
  };

  // If the rowType is "Back", just render the DocumentName
  if (rowType === 'Back') {
    return (
      <Cell fillRow={true}>
        <DocumentName />
      </Cell>
    );
  }

  return (
    <>
      <Row
        id={node.id}
        onClick={(e) => {
          if (rowType === 'Record') {
            if (node.object_type === 'folder') {
              setFilters({ id: node.id, is_trashed: filters.is_trashed }, true);
            }
            if (node.object_type === 'document' && node.file_url) {
              // Instead of opening a new tab, set the file URL to open in a modal popup
              setFileModalUrl(process.env.REACT_APP_URL + node.file_url);
            }
          }
        }}
        index={index}
        hover={rowType === 'Record'}
        pointer={true}
      >
        <Cell
          onClick={(e) => {
            e.stopPropagation();
            e.isPropagationStopped();
          }}
        >
          {rowType === 'Record' && (
            <CheckboxInput
              disabled={!selectable}
              checked={isSelected}
              onChange={() => toggleSelectItem(node)}
            />
          )}
        </Cell>
        <Cell alignItems="start">
          <DocumentName />
        </Cell>
        {!minimal && (
          <>
            {isDesktop && (
              <>
                {!isClient && (
                  <Cell alignItems="start">
                    <span className="line-clamp-3">
                      {(node.clients || [])
                        .map((c) => `${c.given_name} ${c.family_name}`)
                        .join(`\n`)}
                    </span>
                  </Cell>
                )}
                <Cell alignItems="start">
                  <span className="line-clamp-3"> {node.business?.name}</span>
                </Cell>
                <Cell alignItems="start">
                  <span className="line-clamp-3">
                    {(node.projects || []).map((p) => p?.title).join(`\n`) ||
                      node.task?.title}
                  </span>
                </Cell>
              </>
            )}

            <Cell
              className="options-cell"
              justifyContent="center"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                const button = (e.currentTarget as HTMLElement).querySelector(
                  'button',
                ) as HTMLElement;
                if (isAuthenticated) {
                  ActionsPopupRef.openWithContext({
                    anchor: button,
                    data: node,
                  });
                }
              }}
            >
              {isAuthenticated && (
                <button className="p-2">
                  <OptionsIcon className="" />
                </button>
              )}

              {!isAuthenticated && node.object_type === 'document' && (
                <button
                  onClick={() => {
                    saveAs(
                      process.env.REACT_APP_URL + node.file_url,
                      node.title,
                    );
                  }}
                >
                  <DownloadIcon />
                </button>
              )}
            </Cell>
          </>
        )}
      </Row>

      {/* Modal popup for displaying the document */}
      {fileModalUrl && (
        <Modal onClose={() => setFileModalUrl(null)}>
          <iframe
            src={fileModalUrl}
            style={{ width: '100%', height: '100%', border: 'none' }}
            title={node.title}
          />
        </Modal>
      )}
    </>
  );
}
